import { useEffect, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import MediaCard, { MediaCardPost } from "@/components/MediaCard";

type Category =
  | "say_it_in_60"
  | "weekend_huddle"
  | "business_of_womens_sports"
  | "athlete_spotlight"
  | "originals";

type MediaPost = MediaCardPost & { category: Category };

const CATEGORIES: Record<string, { key: Category; label: string; tagline: string }> = {
  "say-it-in-60": { key: "say_it_in_60", label: "Say It In 60", tagline: "Quick video updates — 60 seconds, no fluff." },
  "weekend-huddle": { key: "weekend_huddle", label: "Weekend Huddle", tagline: "Your weekly recap of women's sports." },
  "business-of-womens-sports": { key: "business_of_womens_sports", label: "Business of Women's Sports", tagline: "Deals, deals, and the money moving the game." },
  "athlete-spotlight": { key: "athlete_spotlight", label: "Athlete Spotlight", tagline: "Stories, interviews, and features." },
  originals: { key: "originals", label: "Originals", tagline: "Reporting and features written by Say It In Sports." },
};

const MediaCategory = () => {
  const { category } = useParams<{ category: string }>();
  const meta = category ? CATEGORIES[category] : undefined;
  const [posts, setPosts] = useState<MediaPost[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!meta) return;
    (async () => {
      setLoading(true);
      const { data } = await supabase
        .from("media_posts")
        .select(
          "id, category, content_type, slug, title, subtitle, cover_image_path, thumbnail_url, video_url, published_at",
        )
        .eq("status", "published")
        .eq("category", meta.key)
        .order("published_at", { ascending: false, nullsFirst: false });
      setPosts((data as any) ?? []);
      setLoading(false);
    })();
  }, [meta]);

  if (!meta) return <Navigate to="/media-hub" replace />;

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main>
        <section className="py-16 md:py-24 border-b border-border/50">
          <div className="container">
            <Link to="/media-hub" className="text-xs font-semibold uppercase tracking-[0.2em] text-primary hover:underline">
              ← Media Hub
            </Link>
            <h1 className="text-5xl md:text-7xl mt-3 text-balance">{meta.label}.</h1>
            <p className="text-muted-foreground mt-4 max-w-2xl">{meta.tagline}</p>
          </div>
        </section>

        <section className="py-16 md:py-20">
          <div className="container">
            {loading ? (
              <p className="text-muted-foreground">Loading…</p>
            ) : posts.length === 0 ? (
              <p className="text-muted-foreground">Coming soon...</p>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {posts.map((p) => (
                  <MediaCard key={p.id} post={p} />
                ))}
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default MediaCategory;
